/**
 * Hook to trigger callback when organization changes
 * Used by pages to refresh data when user switches organization
 */
import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Calls the provided callback when organization changes
 * Also returns current organization info
 */
export function useOrganizationChange(callback: () => void) {
  const { currentOrganizationId, currentOrganization } = useAuth();
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Listen for organization change events
  useEffect(() => {
    const handleOrgChange = () => {
      callbackRef.current();
    };

    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, []);

  return {
    currentOrganizationId,
    currentOrganization,
  };
}

/**
 * Simple version that just provides refresh key
 * Use this as a dependency in useEffect to trigger refresh
 */
export function useOrganizationRefreshKey(): number {
  const { currentOrganizationId } = useAuth();
  const keyRef = useRef(0);
  const prevOrgRef = useRef(currentOrganizationId);

  // Increment key when org changes
  if (prevOrgRef.current !== currentOrganizationId) {
    prevOrgRef.current = currentOrganizationId;
    keyRef.current += 1;
  }

  // Also listen for events (in case org changes from elsewhere)
  useEffect(() => {
    const handleOrgChange = () => {
      keyRef.current += 1;
    };

    window.addEventListener('organizationChanged', handleOrgChange);
    return () => window.removeEventListener('organizationChanged', handleOrgChange);
  }, []);

  return keyRef.current;
}
